import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {CardListItem} from "components/Card";
import {ObservableIcon} from "components/EntityIcon";
import {useDeliberateObservables} from "hooks/useDeliberateObservables";
import {sentenceRestLowerCase} from "utils/stringUtils";
import EntityIconLoader from "../../EntityIcon/EntityIconLoader/EntityIconLoader";
import {EntityTitle} from "../Components/EntityTitle";
import PivotMenu from "../Components/PivotMenu";
import css from "./RelationsListItem.module.scss";

const RelationsListItem = ({
  showPivotMenu = true,
  minimal,
  tooltip,
  entity,
  ...otherProps
}) => {
  const {source, related, relation} = entity;

  /**
   * - Clone source & related object, original objects are immutable.
   * - Memoize to help limit re-rendering.
   */
  const resolvedSource = useDeliberateObservables(
    useMemo(() => ({type: source.type, value: source.value}), [source])
  );
  const resolvedRelated = useDeliberateObservables(
    useMemo(() => ({type: related.type, value: related.value}), [related])
  );
  const relationStr = sentenceRestLowerCase(relation);
  const relationTooltip = {
    title: resolvedSource.value,
    subtitle: `${relationStr}: ${resolvedRelated.value}`
  };

  const title = (
    <div>
      <div className={css.relationItem}>
        <span className={css.title}>{resolvedSource.value}</span>

        {showPivotMenu && (
          <PivotMenu
            pivotValues={{
              type: resolvedSource.type,
              value: resolvedSource.value
            }}
          />
        )}
        <span>{relationStr}</span>
      </div>
      <div className={css.relationItem}>
        <span className={css.title}>{resolvedRelated.value}</span>

        {showPivotMenu && (
          <PivotMenu
            pivotValues={{
              type: resolvedRelated.type,
              value: resolvedRelated.value
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <CardListItem
      title={<EntityTitle tooltip={relationTooltip} {...{title, minimal}} />}
      leftSide={
        <div className={css.iconWrapper}>
          {resolvedSource.status === "completed" ? (
            <ObservableIcon {...{...resolvedSource}} />
          ) : (
            <EntityIconLoader />
          )}
          {resolvedRelated.status === "completed" ? (
            <ObservableIcon {...{...resolvedRelated}} />
          ) : (
            <EntityIconLoader />
          )}
        </div>
      }
      entity={entity}
      {...otherProps}
    />
  );
};

RelationsListItem.propTypes = {
  /**
   * Data object for entity
   */
  entity: PropTypes.object,
  /**
   * Default: true.
   *
   * by default a `<PlusMoreTag/>` will be shown below the list if:
   * - `limit` props is defined.
   * - data.length > limit
   *
   * To disable this behavior set showEventsCount={false}
   */
  showPivotMenu: PropTypes.bool,
  /**
   * Default Value: false - Render normal view (Cards, Drawers, etc.)
   * true - Renders a more compact view used by Tables. note that some sections
   * like 'subtitle' and 'rightSide' event counts are never show in this mode.
   */
  minimal: PropTypes.bool,
  /**
   * Remaining Entity properties passed through to CardListItem
   */
  otherProps: PropTypes.object,
  /**
   * Object with `title` and `subtitle` keys to show as tooltip.
   */
  tooltip: PropTypes.object
};

export default React.memo(RelationsListItem);
